import {type LngLat, type LngLatBounds, lngLatsToBounds} from '@yandex-delivery/blocks'
import type {PickupStation} from '@yandex-delivery/types/logistic-dispatcher/external-api'
import {
  applySpec,
  defaultTo,
  firstOf,
  flow,
  get,
  identity,
  ifElse,
  makeGet,
  map,
  pipe,
  raise,
  stubTrue,
} from '@yandex-delivery/utils'

import type {
  NearestRoute,
  PickupPoint,
  PickupPointCity,
  PickupPointFeature,
  PickupPointID,
  PickupPoints,
  PickupPointsWithBounds,
  PickupPointsWithFeatures,
  PickupPointsWithNearest,
  PickupPointType,
} from '../../../model/types'
import {formatSchedule} from '../../../utils/formatSchedule'
import {isValidCoordinates} from '../../../utils/getCoordinates'
import type {fetchPickUpDropOffPoints} from '../index'

export type PickupDropoffRequest = Parameters<typeof fetchPickUpDropOffPoints>[0]
export type PickupDropoffResponse = Awaited<ReturnType<typeof fetchPickUpDropOffPoints>>
export const toPickupDropoffRequest: (city: PickupPointCity) => PickupDropoffRequest = applySpec<PickupDropoffRequest>({
  geo_id: get('id'),
  is_not_branded_partner_station: stubTrue,
})
const customRaise = (msg: string) => (v?: unknown) => {
  raise(msg + v ? ` ${String(v)}` : '')
}
const pickupStationGet = makeGet<PickupStation>()

function toPickupPointType(pickupStation: PickupStation): PickupPointType {
  if (pickupStation.is_market_partner === true) {
    return 'yandex_market'
  }

  return 'pickup_point'
}

const toCoordinates: (pickupStation: PickupStation) => LngLat = flow(
  applySpec<number[]>([pickupStationGet('position.longitude'), pickupStationGet('position.latitude')]),
  ifElse(
    isValidCoordinates, //
    identity as (coordinates: number[]) => LngLat,
    customRaise('[toCoordinates] longitude/longitude invalid'),
  ),
)

function toPickupPoint(pickupStation: PickupStation, scheduleText: string): PickupPoint {
  return {
    _id: pickupStation.id as PickupPointID,
    _type: toPickupPointType(pickupStation),
    coordinates: toCoordinates(pickupStation),
    name: pipe(
      pickupStation,
      firstOf(
        get('address.full_address'), //
        get('name'),
      ),
      defaultTo(''),
    ),
    schedule: pipe(pickupStation, get('schedule'), schedule => formatSchedule(schedule, scheduleText)),
  }
}

function toPickupPointFeature(scheduleText: string) {
  return function _toPickupPointFeature(pickupStation: PickupStation): PickupPointFeature {
    return {
      id: pickupStationGet('id', pickupStation),
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: toCoordinates(pickupStation),
      },
      pickupPoint: toPickupPoint(pickupStation, scheduleText),
    }
  }
}

export function toPickupPoints(source?: LngLat, scheduleText?: string) {
  return function (response?: PickupDropoffResponse): PickupPoints {
    return pipe<
      PickupDropoffResponse | undefined,
      PickupPointsWithFeatures,
      PickupPointsWithFeatures | PickupPointsWithNearest
    >(response, withFeatures(scheduleText ?? ''), source ? withNearest(source) : withBounds)
  }
}

const withFeatures = (scheduleText: string) => {
  return applySpec<PickupPointsWithFeatures>({
    features: map(toPickupPointFeature(scheduleText)),
  })
}

function withBounds(model: PickupPointsWithFeatures): PickupPointsWithFeatures | PickupPointsWithBounds {
  const {features} = model

  if (features.length > 0) {
    return {
      features,
      bounds: pipe(
        features,
        map(get('geometry.coordinates')),
        lngLatsToBounds,
        (bounds?: LngLatBounds): LngLatBounds => {
          return bounds || customRaise('[withBounds] bounds are empty')()
        },
      ),
    }
  }

  return model
}

function withNearest(source: LngLat) {
  return function _withNearest(model: PickupPointsWithFeatures): PickupPointsWithFeatures | PickupPointsWithNearest {
    const {features} = model

    if (features.length > 0) {
      const [sourceX, sourceY] = source
      // ищем ближайший ПВЗ от пользователя
      const {feature} = features.reduce<{distance: number; feature: PickupPointFeature}>(
        (acc, featureItem) => {
          const [featureX, featureY] = featureItem.geometry.coordinates
          // расстояние между двумя точками
          const distance = Math.sqrt((sourceX - featureX) ** 2 + (sourceY - featureY) ** 2)

          if (distance < acc.distance) {
            return {
              distance,
              feature: featureItem,
            }
          }

          return acc
        },
        {distance: Infinity, feature: features[0]},
      )
      const route: NearestRoute = [source, feature.geometry.coordinates]

      return {
        features,
        nearest: {
          feature,
          route,
          bounds: lngLatsToBounds(route, true) || customRaise('[withNearestPoint] cant compute bounds')(),
        },
      }
    }

    return model
  }
}
